import React, { useState, useEffect } from 'react';
import { Card, Spin, Row, Col } from 'antd';
import { DashboardOutlined } from '@ant-design/icons';
import ApiClient from '../../../methods/api/apiClient';
import datepipeModel from '../../../models/datepipemodel';

const COLORS = {
  primary: '#3E549D',
  success: '#27ae60',
  warning: '#f39c12',
  danger: '#e74c3c',
};

const StatBox = ({ value, label, color, icon }) => (
  <div style={{
    textAlign: 'center',
    padding: '16px 8px',
    borderRadius: 8,
    background: '#f9fafb',
    border: '1px solid #f0f0f0',
  }}>
    <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>
      <i className={`fa fa-${icon}`} style={{ color, marginRight: 4 }}></i>
      {label}
    </div>
    <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1.2 }}>
      {value !== null && value !== undefined ? value : '--'}
    </div>
  </div>
);

const TodayOperations = ({ board_id, summaryData }) => {
  const [loading, setLoading] = useState(true);
  const [pickups, setPickups] = useState(null);
  const [deliveries, setDeliveries] = useState(null);
  const [needsAttention, setNeedsAttention] = useState(null);

  useEffect(() => {
    // Use pre-fetched summary data if available
    if (summaryData && summaryData.todayOperations) {
      const data = summaryData.todayOperations;
      setPickups(data.pickups ?? data.pickedup_loads ?? null);
      setDeliveries(data.deliveries ?? data.delivered_loads ?? null);
      setNeedsAttention(data.pending ?? data.needs_attention ?? null);
      setLoading(false);
      return;
    }

    // Fallback: fetch individually if summaryData not provided
    const fetchTodayData = () => {
      setLoading(true);
      const today = datepipeModel.datetostring(new Date());
      const params = { startDate: today, endDate: today };
      if (board_id) params.board_id = board_id;

      ApiClient.get('total-loads', params, '', true)
        .then(res => {
          if (res?.success || res?.code === 200) {
            const data = res?.data || res;
            let finalPickups =
              data.today_pickups ??
              data.todayPickups ??
              data.pickups ??
              data.picked_up ??
              data.pickedUp ??
              null;
            let finalDeliveries =
              data.today_deliveries ??
              data.todayDeliveries ??
              data.deliveries ??
              data.delivered ??
              null;
            let finalAttention =
              data.needs_attention ??
              data.needsAttention ??
              data.no_bids ??
              data.noBids ??
              data.attention ??
              null;

            if (finalPickups === null && data.total_pickup !== undefined) {
              finalPickups = data.total_pickup;
            }
            if (finalDeliveries === null && data.total_delivered !== undefined) {
              finalDeliveries = data.total_delivered;
            }

            setPickups(finalPickups);
            setDeliveries(finalDeliveries);
            setNeedsAttention(finalAttention);
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    };

    fetchTodayData();
  }, [board_id, summaryData]);

  return (
    <Card
      title={
        <span style={{ fontWeight: 600, fontSize: 15 }}>
          <DashboardOutlined style={{ color: COLORS.primary, marginRight: 8 }} />
          Today's Operations
        </span>
      }
      bordered={false}
      style={{ borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
      styles={{ body: { padding: '16px 20px' } }}
    >
      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          <Col xs={8}>
            <StatBox
              value={pickups}
              label="Pickups"
              color={COLORS.primary}
              icon="truck"
            />
          </Col>
          <Col xs={8}>
            <StatBox
              value={deliveries}
              label="Deliveries"
              color={COLORS.success}
              icon="check-circle"
            />
          </Col>
          <Col xs={8}>
            <StatBox
              value={needsAttention}
              label="Needs Attention"
              color={COLORS.danger}
              icon="exclamation-triangle"
            />
          </Col>
        </Row>
        {!loading && pickups === null && deliveries === null && needsAttention === null && (
          <div style={{
            textAlign: 'center',
            color: '#999',
            padding: '8px 0 0',
            fontSize: 12,
          }}>
            Operational data is being collected. Check back later.
          </div>
        )}
      </Spin>
    </Card>
  );
};

export default TodayOperations;
